import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Spin,
  Table,
  Tabs,
  Tag,
  Popconfirm,
  Upload,
  message,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  HolderOutlined,
  DownloadOutlined,
  UploadOutlined,
  CopyOutlined,
  LinkOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { AntPageContainer } from "../../shared/components/AntPageContainer";
import surveyApi, {
  CreateSurveyRequest,
  CreateQuestionRequest,
  CreateSectionRequest,
  MultilingualText,
  QuestionType,
  QUESTION_TYPE_LABELS,
  SUPPORTED_LANGUAGES,
  SurveyDetail,
  SurveyQuestion,
  SurveySection,
} from "../../survey/api";
import useAlert from "../../shared/hooks/useAlert";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import RichTextEditor from "../../shared/components/rich-text-editor";
const { TextArea } = Input;

// ==================== SORTABLE TABLE ROW ====================

interface SortableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-row-key": string;
}

const SortableRow: React.FC<SortableRowProps> = (props) => {
  const { setNodeRef, transform, transition, isDragging } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging
      ? { position: "relative", zIndex: 9999, background: "#fafafa" }
      : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style}>
      {props.children}
    </tr>
  );
};

// ==================== DRAG HANDLE CELL ====================

interface DragHandleCellProps {
  rowKey: string;
}

const DragHandleCell: React.FC<DragHandleCellProps> = ({ rowKey }) => {
  const { listeners, setActivatorNodeRef, attributes } = useSortable({
    id: rowKey,
  });

  return (
    <Button
      type="text"
      size="small"
      ref={setActivatorNodeRef}
      {...listeners}
      {...attributes}
      style={{ cursor: "grab", touchAction: "none" }}
      icon={<HolderOutlined />}
    />
  );
};

// ==================== MULTILINGUAL INPUT COMPONENT ====================

interface MultilingualInputProps {
  value?: MultilingualText;
  onChange?: (value: MultilingualText) => void;
  placeholder?: string;
  textArea?: boolean;
  rows?: number;
  enabledLanguages?: string[];
}

const MultilingualInput: React.FC<MultilingualInputProps> = ({
  value,
  onChange,
  placeholder = "Enter text",
  textArea = false,
  rows = 3,
  enabledLanguages,
}) => {
  // Ensure value is never null/undefined when accessing properties
  const safeValue = value ?? {};

  // Filter languages based on enabled languages, default to all
  const availableLanguages = enabledLanguages
    ? SUPPORTED_LANGUAGES.filter((lang) => enabledLanguages.includes(lang.code))
    : SUPPORTED_LANGUAGES;

  const [activeTab, setActiveTab] = useState<string>(
    availableLanguages[0]?.code || "en"
  );

  // Reset active tab if current tab is no longer available
  React.useEffect(() => {
    if (!availableLanguages.find((lang) => lang.code === activeTab)) {
      setActiveTab(availableLanguages[0]?.code || "en");
    }
  }, [availableLanguages, activeTab]);

  const handleChange = (lang: string, text: string) => {
    onChange?.({ ...safeValue, [lang]: text });
  };

  const InputComponent = textArea ? TextArea : Input;

  if (availableLanguages.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 text-gray-400 text-center">
        No languages selected
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        size="small"
        className="multilingual-tabs"
        items={availableLanguages.map((lang) => ({
          key: lang.code,
          label: (
            <span className={safeValue[lang.code] ? "text-green-600" : ""}>
              {lang.code.toUpperCase()}
              {safeValue[lang.code] ? " ✓" : ""}
            </span>
          ),
          children: (
            <div className="p-3">
              <InputComponent
                value={safeValue[lang.code] || ""}
                onChange={(e) => handleChange(lang.code, e.target.value)}
                placeholder={`${placeholder} (${lang.label})`}
                rows={rows}
              />
            </div>
          ),
        }))}
      />
    </div>
  );
};

// ==================== MULTILINGUAL RICH TEXT INPUT COMPONENT ====================

interface MultilingualRichTextInputProps {
  value?: MultilingualText;
  onChange?: (value: MultilingualText) => void;
  placeholder?: string;
  enabledLanguages?: string[];
}

const MultilingualRichTextInput: React.FC<MultilingualRichTextInputProps> = ({
  value,
  onChange,
  placeholder = "Enter text",
  enabledLanguages,
}) => {
  // Handle null/undefined values by defaulting to empty object
  const safeValue = value ?? {};
  // Filter languages based on enabled languages, default to all
  const availableLanguages = enabledLanguages
    ? SUPPORTED_LANGUAGES.filter((lang) => enabledLanguages.includes(lang.code))
    : SUPPORTED_LANGUAGES;

  const [activeTab, setActiveTab] = useState<string>(
    availableLanguages[0]?.code || "en"
  );

  // Reset active tab if current tab is no longer available
  React.useEffect(() => {
    if (!availableLanguages.find((lang) => lang.code === activeTab)) {
      setActiveTab(availableLanguages[0]?.code || "en");
    }
  }, [availableLanguages, activeTab]);

  const handleChange = (lang: string, text: string) => {
    onChange?.({ ...safeValue, [lang]: text });
  };

  // Helper to check if HTML content is empty
  const isContentEmpty = (html: string | undefined): boolean => {
    if (!html) return true;
    const stripped = html.replace(/<[^>]*>/g, "").trim();
    return stripped === "";
  };

  if (availableLanguages.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 text-gray-400 text-center">
        No languages selected
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        size="small"
        className="multilingual-tabs"
        items={availableLanguages.map((lang) => ({
          key: lang.code,
          label: (
            <span
              className={
                !isContentEmpty(safeValue[lang.code]) ? "text-green-600" : ""
              }
            >
              {lang.code.toUpperCase()}
              {!isContentEmpty(safeValue[lang.code]) ? " ✓" : ""}
            </span>
          ),
          children: (
            <div className="p-3">
              <RichTextEditor
                value={safeValue[lang.code] || ""}
                onChange={(html) => handleChange(lang.code, html)}
                placeholder={`${placeholder} (${lang.label})`}
              />
            </div>
          ),
        }))}
      />
    </div>
  );
};

// ==================== SORTABLE OPTION ITEM ====================

interface SortableOptionProps {
  id: string;
  option: { text: MultilingualText; order: number; dbId?: number };
  index: number;
  onUpdate: (index: number, text: MultilingualText) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  enabledLanguages?: string[];
}

const SortableOption: React.FC<SortableOptionProps> = ({
  id,
  option,
  index,
  onUpdate,
  onRemove,
  canRemove,
  enabledLanguages,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex gap-2 items-start bg-white"
    >
      <Button
        type="text"
        size="small"
        {...attributes}
        {...listeners}
        style={{ cursor: "grab", touchAction: "none", marginTop: 8 }}
        icon={<HolderOutlined />}
      />
      <div className="flex-1">
        <MultilingualInput
          value={option.text}
          onChange={(text) => onUpdate(index, text)}
          placeholder={`Option ${index + 1}`}
          enabledLanguages={enabledLanguages}
        />
      </div>
      {canRemove && (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onRemove(index)}
          className="mt-2"
        />
      )}
    </div>
  );
};

// ==================== QUESTION MODAL ====================

interface QuestionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (question: CreateQuestionRequest) => Promise<void>;
  editingQuestion?: SurveyQuestion | null;
  enabledLanguages?: string[];
  allQuestions?: SurveyQuestion[]; // All questions in the survey for conditional selection
}

const QuestionModal: React.FC<QuestionModalProps> = ({
  open,
  onClose,
  onSave,
  editingQuestion,
  enabledLanguages,
  allQuestions = [],
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [questionType, setQuestionType] = useState<QuestionType>(
    QuestionType.SHORT_TEXT
  );
  const [hintText, setHintText] = useState<MultilingualText>({});
  const [options, setOptions] = useState<
    Array<{ id: string; text: MultilingualText; order: number; dbId?: number }>
  >([]);
  const [allowOther, setAllowOther] = useState(false);
  // Answer count for short text questions
  const [answerCount, setAnswerCount] = useState<number>(1);
  // Max selectable options for multi-select questions (null = no limit)
  const [maxSelections, setMaxSelections] = useState<number | null>(null);
  // Conditional question state
  const [isConditional, setIsConditional] = useState(false);
  const [conditionQuestionId, setConditionQuestionId] = useState<number | null>(
    null
  );
  const [conditionOptionIds, setConditionOptionIds] = useState<number[]>([]);

  // Sensors for drag and drop
  const optionSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Generate unique ID for options
  const generateOptionId = () =>
    `option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  useEffect(() => {
    if (open) {
      if (editingQuestion) {
        form.setFieldsValue({
          title: editingQuestion.title,
          type: editingQuestion.type,
          isRequired: editingQuestion.isRequired,
        });
        setQuestionType(editingQuestion.type);
        setHintText(editingQuestion.hintText || {});
        // Set answer count for short text questions
        setAnswerCount(editingQuestion.answerCount || 1);
        setMaxSelections(editingQuestion.maxSelections ?? null);
        // Check if there's an "Other" option and filter it from visible options
        const hasOtherOption = editingQuestion.options.some(
          (o) => o.isOtherOption
        );
        setAllowOther(hasOtherOption);
        setOptions(
          editingQuestion.options
            .filter((o) => !o.isOtherOption) // Don't show "Other" option in the list
            .map((o) => ({
              id: generateOptionId(),
              text: o.text,
              order: o.order,
              dbId: o.id, // Preserve the database ID
            }))
        );
        // Set conditional fields
        const hasCondition =
          editingQuestion.conditionQuestionId &&
          editingQuestion.conditionOptionIds?.length;
        setIsConditional(!!hasCondition);
        setConditionQuestionId(editingQuestion.conditionQuestionId || null);
        setConditionOptionIds(editingQuestion.conditionOptionIds || []);
      } else {
        form.resetFields();
        setQuestionType(QuestionType.SHORT_TEXT);
        setHintText({});
        setOptions([]);
        setAllowOther(false);
        // Reset answer count
        setAnswerCount(1);
        setMaxSelections(null);
        // Reset conditional fields
        setIsConditional(false);
        setConditionQuestionId(null);
        setConditionOptionIds([]);
      }
    }
  }, [open, editingQuestion, form]);

  const handleTypeChange = (type: QuestionType) => {
    setQuestionType(type);
    if (
      type === QuestionType.MULTIPLE_CHOICE ||
      type === QuestionType.MULTI_SELECT
    ) {
      if (options.length === 0) {
        setOptions([
          { id: generateOptionId(), text: { en: "" }, order: 0 },
          { id: generateOptionId(), text: { en: "" }, order: 1 },
        ]);
      }
    }
  };

  const addOption = () => {
    setOptions([
      ...options,
      { id: generateOptionId(), text: { en: "" }, order: options.length },
    ]);
  };

  const removeOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions.map((o, i) => ({ ...o, order: i })));
  };

  const updateOption = (index: number, text: MultilingualText) => {
    const newOptions = [...options];
    newOptions[index].text = text;
    setOptions(newOptions);
  };

  // Handle drag end for option reordering
  const handleOptionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = options.findIndex((o) => o.id === active.id);
    const newIndex = options.findIndex((o) => o.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedOptions = arrayMove(options, oldIndex, newIndex);
    setOptions(reorderedOptions.map((o, i) => ({ ...o, order: i })));
  };

  // "Other" is also a selectable checkbox on the public form
  const selectableOptionCount = options.length + (allowOther ? 1 : 0);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const question: CreateQuestionRequest = {
        title: values.title,
        // Null (not undefined) so clearing every language also clears the saved hint
        hintText: Object.values(hintText).some((v) => v?.trim())
          ? hintText
          : null,
        type: questionType,
        isRequired: values.isRequired ?? true,
        // Include answer count for short text questions
        answerCount:
          questionType === QuestionType.SHORT_TEXT
            ? Math.max(1, answerCount)
            : undefined,
        // Null clears the limit (also when switching away from multi-select)
        maxSelections:
          questionType === QuestionType.MULTI_SELECT ? maxSelections : null,
        // Include conditional fields
        conditionQuestionId:
          isConditional && conditionQuestionId ? conditionQuestionId : null,
        conditionOptionIds:
          isConditional && conditionOptionIds.length > 0
            ? conditionOptionIds
            : null,
      };

      if (
        questionType === QuestionType.MULTIPLE_CHOICE ||
        questionType === QuestionType.MULTI_SELECT
      ) {
        if (options.length < 2) {
          message.error("Choice questions must have at least 2 options");
          return;
        }
        if (
          questionType === QuestionType.MULTI_SELECT &&
          maxSelections !== null &&
          maxSelections > selectableOptionCount
        ) {
          message.error(
            `Maximum selections cannot exceed the number of options (${selectableOptionCount})`
          );
          return;
        }
        // Include database ID for existing options, text and order for all
        const regularOptions: Array<{
          id?: number;
          text: MultilingualText;
          order: number;
          isOtherOption?: boolean;
        }> = options.map(({ text, order, dbId }) => ({
          id: dbId, // Database ID (undefined for new options)
          text,
          order,
        }));

        // If allowOther is enabled, append the "Other" option at the end
        if (allowOther) {
          // Find existing "Other" option ID if editing
          const existingOtherOption = editingQuestion?.options.find(
            (o) => o.isOtherOption
          );
          regularOptions.push({
            id: existingOtherOption?.id, // Preserve ID if updating
            text: {
              en: "Other (please specify)",
              zh: "其他（请注明）",
              ko: "기타 (구체적으로 명시해주세요)",
              vi: "Khác (vui lòng nêu rõ)",
              fr: "Autre (veuillez préciser)",
              th: "อื่นๆ (โปรดระบุ)",
              es: "Otro (por favor especifique)",
              ru: "Другое (пожалуйста, уточните)",
              hi: "अन्य (कृपया विशिष्ट करें)",
            },
            order: options.length, // Last position
            isOtherOption: true,
          });
        }

        question.options = regularOptions;
      }

      await onSave(question);
      onClose();
    } catch (error) {
      console.error("Error saving question:", error);
    } finally {
      setLoading(false);
    }
  };

  const needsOptions =
    questionType === QuestionType.MULTIPLE_CHOICE ||
    questionType === QuestionType.MULTI_SELECT;

  return (
    <Modal
      title={editingQuestion ? "Edit Question" : "Add Question"}
      open={open}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          loading={loading}
          onClick={handleSave}
        >
          {editingQuestion ? "Update Question" : "Add Question"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" initialValues={{ isRequired: true }}>
        <Form.Item
          name="title"
          label="Question Title"
          rules={[{ required: true, message: "Please enter the question" }]}
        >
          <MultilingualInput
            placeholder="Enter question"
            enabledLanguages={enabledLanguages}
          />
        </Form.Item>

        <Form.Item
          name="type"
          label="Question Type"
          initialValue={questionType}
        >
          <Select
            value={questionType}
            onChange={handleTypeChange}
            options={Object.entries(QUESTION_TYPE_LABELS).map(
              ([value, label]) => ({
                value,
                label,
              })
            )}
          />
        </Form.Item>

        {/* Answer Count - only show for Short Text questions */}
        {questionType === QuestionType.SHORT_TEXT && (
          <Form.Item
            label={
              <span>
                Number of Answer Boxes{" "}
                <span className="text-gray-400 text-sm font-normal">
                  (for questions like "mention 2 things you like")
                </span>
              </span>
            }
          >
            <InputNumber
              min={1}
              max={10}
              value={answerCount}
              onChange={(value) => setAnswerCount(value || 1)}
              className="w-32"
            />
          </Form.Item>
        )}

        <Form.Item name="isRequired" valuePropName="checked" label="Required">
          <Switch defaultChecked />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Hint Text{" "}
              <span className="text-gray-400 text-sm font-normal">
                (optional - displayed below the question)
              </span>
            </span>
          }
        >
          <MultilingualInput
            value={hintText}
            onChange={setHintText}
            placeholder="Enter hint text"
            enabledLanguages={enabledLanguages}
          />
        </Form.Item>

        {needsOptions && (
          <div className="mt-4">
            <div className="flex justify-between items-center mb-3">
              <span className="font-medium">Answer Options</span>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addOption}
                size="small"
              >
                Add Option
              </Button>
            </div>
            <DndContext
              sensors={optionSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleOptionDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={options.map((o) => o.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {options.map((option, index) => (
                    <SortableOption
                      key={option.id}
                      id={option.id}
                      option={option}
                      index={index}
                      onUpdate={updateOption}
                      onRemove={removeOption}
                      canRemove={options.length > 2}
                      enabledLanguages={enabledLanguages}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Allow Other Answers Toggle */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <Checkbox
                checked={allowOther}
                onChange={(e) => setAllowOther(e.target.checked)}
              >
                <span className="text-gray-700">
                  Allow "Other" answers{" "}
                  <span className="text-gray-400 text-sm">
                    (adds "Other (please specify)" option with text input)
                  </span>
                </span>
              </Checkbox>
            </div>

            {/* Max Selections - only for Multi Select questions */}
            {questionType === QuestionType.MULTI_SELECT && (
              <div className="mt-4">
                <label className="block font-medium mb-1">
                  Maximum Selections{" "}
                  <span className="text-gray-400 text-sm font-normal">
                    (optional - leave empty for no limit)
                  </span>
                </label>
                <InputNumber
                  min={1}
                  max={selectableOptionCount}
                  precision={0}
                  value={maxSelections}
                  onChange={(value) => setMaxSelections(value ?? null)}
                  placeholder="No limit"
                  className="w-32"
                  status={
                    maxSelections !== null &&
                    maxSelections > selectableOptionCount
                      ? "error"
                      : undefined
                  }
                />
                {maxSelections !== null &&
                  maxSelections > selectableOptionCount && (
                    <p className="text-red-500 text-sm mt-2">
                      Cannot exceed the number of options (
                      {selectableOptionCount})
                    </p>
                  )}
              </div>
            )}
          </div>
        )}

        {/* Conditional Question Section */}
        {(() => {
          // Get available questions for condition (only MULTIPLE_CHOICE or MULTI_SELECT, excluding the current question)
          const availableConditionQuestions = allQuestions.filter(
            (q) =>
              (q.type === QuestionType.MULTIPLE_CHOICE ||
                q.type === QuestionType.MULTI_SELECT) &&
              q.id !== editingQuestion?.id
          );

          // Get options for the selected condition question
          const selectedConditionQuestion = availableConditionQuestions.find(
            (q) => q.id === conditionQuestionId
          );

          return availableConditionQuestions.length > 0 ? (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Switch
                  checked={isConditional}
                  onChange={(checked) => {
                    setIsConditional(checked);
                    if (!checked) {
                      setConditionQuestionId(null);
                      setConditionOptionIds([]);
                    }
                  }}
                />
                <span className="font-medium">Conditional Question</span>
                <span className="text-gray-400 text-sm">
                  (only show this question based on another answer)
                </span>
              </div>

              {isConditional && (
                <div className="space-y-4 pl-4 border-l-2 border-blue-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Show this question when the answer to:
                    </label>
                    <Select
                      placeholder="Select a question"
                      value={conditionQuestionId}
                      onChange={(value) => {
                        setConditionQuestionId(value);
                        setConditionOptionIds([]); // Reset selected options when question changes
                      }}
                      className="w-full"
                      options={availableConditionQuestions.map((q) => ({
                        value: q.id,
                        label:
                          q.title.en ||
                          Object.values(q.title).find((v) => v) ||
                          `Question ${q.id}`,
                      }))}
                    />
                  </div>

                  {conditionQuestionId && selectedConditionQuestion && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Is one of these options:
                      </label>
                      <Checkbox.Group
                        value={conditionOptionIds}
                        onChange={(values) =>
                          setConditionOptionIds(values as number[])
                        }
                        className="flex flex-col gap-2"
                      >
                        {selectedConditionQuestion.options
                          .sort((a, b) => a.order - b.order)
                          .map((option) => (
                            <Checkbox key={option.id} value={option.id}>
                              {option.text.en ||
                                Object.values(option.text).find((v) => v) ||
                                `Option ${option.id}`}
                            </Checkbox>
                          ))}
                      </Checkbox.Group>
                      {conditionOptionIds.length === 0 && (
                        <p className="text-amber-500 text-sm mt-2">
                          Please select at least one option
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null;
        })()}
      </Form>
    </Modal>
  );
};

// ==================== SORTABLE SECTION CARD ====================

interface SortableSectionCardProps {
  section: SurveySection;
  sectionIndex: number;
  children: React.ReactNode;
  onEdit: () => void;
  onDelete: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const SortableSectionCard: React.FC<SortableSectionCardProps> = ({
  section,
  sectionIndex,
  children,
  onEdit,
  onDelete,
  isExpanded,
  onToggleExpand,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id!.toString() });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const questionCount = section.questions?.length || 0;

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        type="inner"
        bodyStyle={!isExpanded ? { padding: 0 } : {}}
        title={
          <div
            className="flex items-center gap-2 cursor-pointer select-none"
            onClick={onToggleExpand}
          >
            <Button
              type="text"
              size="small"
              {...listeners}
              {...attributes}
              style={{ cursor: "grab", touchAction: "none" }}
              icon={<HolderOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
            <Button
              type="text"
              size="small"
              icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
              className="text-gray-500"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
            />
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
              Section {sectionIndex + 1}
            </span>
            {section.title &&
            (section.title.en ||
              Object.values(section.title).find((v) => v)) ? (
              <span className="font-medium">
                {section.title.en ||
                  Object.values(section.title).find((v) => v)}
              </span>
            ) : (
              <></>
            )}
            <span className="text-gray-400 text-sm font-normal">
              ({questionCount} {questionCount === 1 ? "question" : "questions"})
            </span>
          </div>
        }
        extra={
          <Space onClick={(e) => e.stopPropagation()}>
            <Button type="text" icon={<EditOutlined />} onClick={onEdit} />
            <Popconfirm
              title="Delete Section"
              description="This will delete the section and all its questions. Are you sure?"
              onConfirm={onDelete}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        }
      >
        {isExpanded && children}
      </Card>
    </div>
  );
};

// ==================== SECTION MODAL ====================

interface SectionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (section: CreateSectionRequest) => Promise<void>;
  editingSection?: SurveySection | null;
  enabledLanguages?: string[];
}

const SectionModal: React.FC<SectionModalProps> = ({
  open,
  onClose,
  onSave,
  editingSection,
  enabledLanguages,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (editingSection) {
        form.setFieldsValue({
          title: editingSection.title,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, editingSection, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const section: CreateSectionRequest = {
        title: values.title,
      };

      await onSave(section);
      onClose();
    } catch (error) {
      console.error("Error saving section:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={editingSection ? "Edit Section" : "Add Section"}
      open={open}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          loading={loading}
          onClick={handleSave}
        >
          {editingSection ? "Update Section" : "Add Section"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="title"
          label={
            <span>
              Section Title{" "}
              <span className="text-gray-400 text-sm font-normal">
                (optional - leave empty for no title)
              </span>
            </span>
          }
        >
          <MultilingualInput
            placeholder="Enter section title"
            enabledLanguages={enabledLanguages}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ==================== MAIN FORM ====================

export const SurveyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [survey, setSurvey] = useState<SurveyDetail | null>(null);
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<SurveyQuestion | null>(
    null
  );
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<SurveySection | null>(
    null
  );
  const [currentSectionId, setCurrentSectionId] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>(
    SUPPORTED_LANGUAGES.map((lang) => lang.code)
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [channels, setChannels] = useState<string[]>([]);
  const [channelInput, setChannelInput] = useState<string>("");
  const [exportingTranslations, setExportingTranslations] = useState(false);
  const [importingTranslations, setImportingTranslations] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>("");
  const [logoImageUrl, setLogoImageUrl] = useState<string>("");
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [successButtonUrl, setSuccessButtonUrl] = useState<string>("");
  const [expandedSections, setExpandedSections] = useState<Set<number>>(
    new Set()
  );

  // Ref for preserving scroll position
  const scrollPositionRef = useRef<number | null>(null);
  // Ref to store initial form values for comparison
  const initialValuesRef = useRef<{
    title?: MultilingualText;
    welcomeMessage?: MultilingualText;
    successMessage?: MultilingualText;
    successButtonLabel?: MultilingualText;
    successButtonUrl?: string;
    isActive?: boolean;
        supportedLanguages?: string[];
        channels?: string[];
        backgroundImageUrl?: string;
    logoImageUrl?: string;
  } | null>(null);

  const isEditing = !!id;

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Validation function to check if at least one language has a title (welcome message and success message are optional)
  const validateFormCompletion = useCallback(
    (
      title?: MultilingualText,
      _welcomeMessage?: MultilingualText,
      _successMessage?: MultilingualText,
      languages?: string[]
    ) => {
      const langs = languages || supportedLanguages;
      // Check if at least one supported language has title filled (welcome message is optional)
      const hasValidLanguage = langs.some((langCode) =>
        title?.[langCode as keyof MultilingualText]?.trim()
      );
      setIsFormValid(hasValidLanguage);
    },
    [supportedLanguages]
  );

  const loadSurvey = useCallback(
    async (surveyId: number, preserveScroll = false) => {
      try {
        if (preserveScroll) {
          scrollPositionRef.current = window.scrollY;
        }
        setLoading(true);
        const response = await surveyApi.getById(surveyId);
        setSurvey(response.data ?? null);
        const formValues = {
          title: response.data?.title,
          welcomeMessage: response.data?.welcomeMessage,
          successMessage: response.data?.successMessage,
          successButtonLabel: response.data?.successButtonLabel,
          isActive: response.data?.isActive,
        };
        form.setFieldsValue(formValues);
        // Set success button URL from survey
        const surveySuccessButtonUrl = response.data?.successButtonUrl || "";
        setSuccessButtonUrl(surveySuccessButtonUrl);
        // Set supported languages from survey
        const surveyLanguages =
          response.data?.supportedLanguages ||
          SUPPORTED_LANGUAGES.map((l) => l.code);
        setSupportedLanguages(surveyLanguages);
        // Set channels from survey
        const surveyChannels = response.data?.channels || [];
        setChannels(surveyChannels);
        // Set branding images from survey
        const surveyBackgroundImageUrl =
          response.data?.backgroundImageUrl || "";
        const surveyLogoImageUrl = response.data?.logoImageUrl || "";
        setBackgroundImageUrl(surveyBackgroundImageUrl);
        setLogoImageUrl(surveyLogoImageUrl);
        // Store initial values for change detection
        initialValuesRef.current = {
          ...formValues,
          supportedLanguages: surveyLanguages,
          channels: surveyChannels,
          backgroundImageUrl: surveyBackgroundImageUrl,
          logoImageUrl: surveyLogoImageUrl,
          successButtonUrl: surveySuccessButtonUrl,
        };
        setHasChanges(false);
        // Validate form initially
        validateFormCompletion(
          formValues.title,
          formValues.welcomeMessage,
          formValues.successMessage,
          surveyLanguages
        );
      } catch (error) {
        console.error("Error loading survey:", error);
        setAlert("Error loading survey", "error");
        navigate("/admin/surveys");
      } finally {
        setLoading(false);
      }
    },
    [form, setAlert, navigate, validateFormCompletion]
  );

  // Use a ref to hold the latest loadSurvey function to avoid stale closure issues
  const loadSurveyRef = useRef(loadSurvey);
  loadSurveyRef.current = loadSurvey;

  // Initial load - only depends on id, uses ref to avoid re-triggering on callback changes
  useEffect(() => {
    if (id) {
      loadSurveyRef.current(parseInt(id));
    }
  }, [id]);

  // Check if form values have changed from initial values
  const checkForChanges = useCallback(
    (changedValues: any, allValues: any) => {
      // Validate form completion
      validateFormCompletion(
        allValues.title,
        allValues.welcomeMessage,
        allValues.successMessage
      );

      if (!initialValuesRef.current) {
        // For new surveys, always allow saving if valid
        setHasChanges(true);
        return;
      }

      const initial = initialValuesRef.current;
      const hasChanged =
        JSON.stringify(allValues.title) !== JSON.stringify(initial.title) ||
        JSON.stringify(allValues.welcomeMessage) !==
          JSON.stringify(initial.welcomeMessage) ||
        JSON.stringify(allValues.successMessage) !==
          JSON.stringify(initial.successMessage) ||
        JSON.stringify(allValues.successButtonLabel) !==
          JSON.stringify(initial.successButtonLabel) ||
        allValues.isActive !== initial.isActive ||
        JSON.stringify(supportedLanguages) !==
          JSON.stringify(initial.supportedLanguages) ||
        JSON.stringify(channels) !== JSON.stringify(initial.channels) ||
        backgroundImageUrl !== initial.backgroundImageUrl ||
        logoImageUrl !== initial.logoImageUrl ||
        successButtonUrl !== initial.successButtonUrl;

      setHasChanges(hasChanged);
    },
    [
      validateFormCompletion,
      supportedLanguages,
      channels,
      backgroundImageUrl,
      logoImageUrl,
      successButtonUrl,
    ]
  );

  // Restore scroll position after survey loads
  useEffect(() => {
    if (!loading && scrollPositionRef.current !== null) {
      window.scrollTo(0, scrollPositionRef.current);
      scrollPositionRef.current = null;
    }
  }, [loading]);

  // Revalidate form when supportedLanguages change
  useEffect(() => {
    const values = form.getFieldsValue();
    validateFormCompletion(
      values.title,
      values.welcomeMessage,
      values.successMessage
    );
    // Also trigger change detection
    if (initialValuesRef.current) {
      const hasLanguageChange =
        JSON.stringify(supportedLanguages) !==
        JSON.stringify(initialValuesRef.current.supportedLanguages);
      if (hasLanguageChange) {
        setHasChanges(true);
      }
    }
  }, [supportedLanguages, form, validateFormCompletion]);

  // Handler for language checkbox changes
  const handleLanguageChange = (langCode: string, checked: boolean) => {
    if (checked) {
      setSupportedLanguages((prev) => [...prev, langCode]);
    } else {
      // Don't allow unchecking the last language
      if (supportedLanguages.length > 1) {
        setSupportedLanguages((prev) => prev.filter((l) => l !== langCode));
      } else {
        message.warning("At least one language must be selected");
      }
    }
  };

  // Handle drag end for reordering questions within a section
  const handleSectionQuestionDragEnd = useCallback(
    async (sectionId: number, event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id || !survey) return;

      const section = survey.sections?.find((s) => s.id === sectionId);
      if (!section || !section.questions) return;

      const sortedQuestions = [...section.questions].sort(
        (a, b) => a.order - b.order
      );
      const oldIndex = sortedQuestions.findIndex(
        (q) => q.id?.toString() === active.id
      );
      const newIndex = sortedQuestions.findIndex(
        (q) => q.id?.toString() === over.id
      );

      if (oldIndex === -1 || newIndex === -1) return;

      const reorderedQuestions = arrayMove(sortedQuestions, oldIndex, newIndex);

      // Optimistically update UI
      setSurvey({
        ...survey,
        sections: survey.sections?.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                questions: reorderedQuestions.map((q, i) => ({
                  ...q,
                  order: i,
                })),
              }
            : s
        ),
      });

      // Save new order to backend
      try {
        setReordering(true);
        const questionOrder = reorderedQuestions.map((q, i) => ({
          id: q.id!,
          order: i,
        }));
        await surveyApi.reorderQuestions(survey.id, questionOrder);
      } catch (error) {
        console.error("Error reordering questions:", error);
        setAlert("Error reordering questions", "error");
        // Revert on error
        loadSurvey(survey.id, true);
      } finally {
        setReordering(false);
      }
    },
    [survey, setAlert, loadSurvey]
  );

  // Auto-save channels when adding or removing (only for editing existing surveys)
  const saveChannels = useCallback(
    async (newChannels: string[]) => {
      if (!isEditing || !survey) return;
      try {
        await surveyApi.update(survey.id, { channels: newChannels });
        setAlert("Channels updated", "success");
      } catch (error) {
        console.error("Error updating channels:", error);
        setAlert("Error updating channels", "error");
      }
    },
    [isEditing, survey, setAlert]
  );

  const handleSubmit = async (values: any) => {
    try {
      setSaving(true);

      if (isEditing && survey) {
        await surveyApi.update(survey.id, {
          title: values.title,
          welcomeMessage: values.welcomeMessage,
          successMessage: values.successMessage,
          successButtonLabel: values.successButtonLabel,
          successButtonUrl,
          isActive: values.isActive,
          supportedLanguages,
          channels,
          backgroundImageUrl,
          logoImageUrl,
        });
        setAlert("Survey updated successfully!", "success");
        loadSurvey(survey.id);
      } else {
        const request: CreateSurveyRequest = {
          title: values.title,
          welcomeMessage: values.welcomeMessage,
          successMessage: values.successMessage,
          successButtonLabel: values.successButtonLabel,
          successButtonUrl: successButtonUrl || undefined, // OK for create - no previous value to clear
          isActive: values.isActive ?? true,
          supportedLanguages,
          channels: channels.length > 0 ? channels : undefined,
          backgroundImageUrl: backgroundImageUrl || undefined,
          logoImageUrl: logoImageUrl || undefined,
        };
        const response = await surveyApi.create(request);
        setAlert("Survey created successfully!", "success");
        navigate(`/admin/surveys/edit/${response.data?.id}`);
      }
    } catch (error) {
      console.error("Error saving survey:", error);
      setAlert("Error saving survey", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddQuestion = async (question: CreateQuestionRequest) => {
    if (!survey) return;
    try {
      await surveyApi.addQuestion(survey.id, {
        ...question,
        order: survey.questions.length,
      });
      setAlert("Question added successfully!", "success");
      loadSurvey(survey.id, true); // Preserve scroll position
    } catch (error) {
      console.error("Error adding question:", error);
      setAlert("Error adding question", "error");
      throw error;
    }
  };

  const handleUpdateQuestion = async (question: CreateQuestionRequest) => {
    if (!editingQuestion) return;
    try {
      await surveyApi.updateQuestion(editingQuestion.id!, question);
      setAlert("Question updated successfully!", "success");
      loadSurvey(survey!.id, true); // Preserve scroll position
    } catch (error) {
      console.error("Error updating question:", error);
      setAlert("Error updating question", "error");
      throw error;
    }
  };

  const handleDeleteQuestion = async (questionId: number) => {
    try {
      await surveyApi.deleteQuestion(questionId);
      setAlert("Question deleted successfully!", "success");
      loadSurvey(survey!.id, true); // Preserve scroll position
    } catch (error) {
      console.error("Error deleting question:", error);
      setAlert("Error deleting question", "error");
    }
  };

  // ==================== SECTION HANDLERS ====================

  const handleAddSection = async (section: CreateSectionRequest) => {
    if (!survey) return;
    try {
      const response = await surveyApi.addSection(survey.id, {
        ...section,
        order: survey.sections?.length || 0,
      });
      // Expand the newly created section
      if (response.data?.id) {
        setExpandedSections((prev) => {
          const next = new Set(Array.from(prev));
          next.add(response.data!.id!);
          return next;
        });
      }
      setAlert("Section added successfully!", "success");
      loadSurvey(survey.id, true);
    } catch (error) {
      console.error("Error adding section:", error);
      setAlert("Error adding section", "error");
      throw error;
    }
  };

  const handleUpdateSection = async (section: CreateSectionRequest) => {
    if (!editingSection) return;
    try {
      await surveyApi.updateSection(editingSection.id!, section);
      setAlert("Section updated successfully!", "success");
      loadSurvey(survey!.id, true);
    } catch (error) {
      console.error("Error updating section:", error);
      setAlert("Error updating section", "error");
      throw error;
    }
  };

  const handleDeleteSection = async (sectionId: number) => {
    try {
      await surveyApi.deleteSection(sectionId);
      setAlert("Section deleted successfully!", "success");
      loadSurvey(survey!.id, true);
    } catch (error) {
      console.error("Error deleting section:", error);
      setAlert("Error deleting section", "error");
    }
  };

  // Handle drag end for section reordering
  const handleSectionDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id || !survey || !survey.sections) return;

      const sortedSections = [...survey.sections].sort(
        (a, b) => a.order - b.order
      );
      const oldIndex = sortedSections.findIndex(
        (s) => s.id!.toString() === active.id
      );
      const newIndex = sortedSections.findIndex(
        (s) => s.id!.toString() === over.id
      );

      if (oldIndex === -1 || newIndex === -1) return;

      const reorderedSections = arrayMove(sortedSections, oldIndex, newIndex);

      // Optimistically update UI
      setSurvey({
        ...survey,
        sections: reorderedSections.map((s, i) => ({
          ...s,
          order: i,
        })),
      });

      // Persist to backend
      setReordering(true);
      try {
        await surveyApi.reorderSections(
          survey.id,
          reorderedSections.map((s, i) => ({ id: s.id!, order: i }))
        );
      } catch (error) {
        console.error("Error reordering sections:", error);
        setAlert("Error reordering sections", "error");
        loadSurvey(survey.id, true);
      } finally {
        setReordering(false);
      }
    },
    [survey, setAlert, loadSurvey]
  );

  const handleAddQuestionToSection = (sectionId: number) => {
    setCurrentSectionId(sectionId);
    setEditingQuestion(null);
    setQuestionModalOpen(true);
  };

  // Toggle section expansion
  const toggleSectionExpanded = (sectionId: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // ==================== TRANSLATION HANDLERS ====================

  const handleExportTranslations = async () => {
    if (!survey) return;
    try {
      setExportingTranslations(true);
      await surveyApi.exportTranslations(survey.id, survey.publicId);
      setAlert("Translations exported successfully!", "success");
    } catch (error) {
      console.error("Error exporting translations:", error);
      setAlert("Error exporting translations", "error");
    } finally {
      setExportingTranslations(false);
    }
  };

  const handleImportTranslations = async (file: File) => {
    if (!survey) return;
    try {
      setImportingTranslations(true);
      const result = await surveyApi.importTranslations(survey.id, file);

      if (result.data) {
        const { updated, skipped, errors } = result.data;
        if (errors && errors.length > 0) {
          setAlert(
            `Translations imported: ${updated} updated, ${skipped} skipped. Some errors occurred.`,
            "warning"
          );
          console.warn("Import errors:", errors);
        } else {
          setAlert(
            `Translations imported successfully! ${updated} updated, ${skipped} skipped.`,
            "success"
          );
        }
        // Reload the survey to show updated translations
        loadSurvey(survey.id, true);
      }
    } catch (error) {
      console.error("Error importing translations:", error);
      setAlert("Error importing translations", "error");
    } finally {
      setImportingTranslations(false);
    }
  };

  const handleAddQuestionWithSection = async (
    question: CreateQuestionRequest
  ) => {
    if (!survey || currentSectionId === null) return;
    try {
      // Find the section to get the current question count
      const section = survey.sections?.find((s) => s.id === currentSectionId);
      const questionCount = section?.questions?.length || 0;

      await surveyApi.addQuestion(survey.id, {
        ...question,
        sectionId: currentSectionId,
        order: questionCount,
      });
      setAlert("Question added successfully!", "success");
      loadSurvey(survey.id, true);
    } catch (error) {
      console.error("Error adding question:", error);
      setAlert("Error adding question", "error");
      throw error;
    }
  };

  if (loading) {
    return (
      <AntPageContainer>
        <div className="w-full h-[50vh] flex justify-center items-center">
          <Spin size="large" />
        </div>
      </AntPageContainer>
    );
  }

  return (
    <AntPageContainer>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/admin/surveys")}
          >
            Back to Surveys
          </Button>
        </div>

        <h1 className="text-2xl font-bold mb-6">
          {isEditing ? "Edit Survey" : "Create New Survey"}
        </h1>

        {/* Survey Details Form */}
        <Card title="Survey Details" className="mb-6">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={checkForChanges}
            initialValues={{ isActive: true }}
          >
            <Form.Item
              label={
                <span>
                  Channels{" "}
                  <span className="text-gray-400 text-sm font-normal">
                    (optional - each channel generates a unique survey link)
                  </span>
                </span>
              }
            >
              <div className="space-y-2">
                {/* Input to add new channel */}
                <div className="flex gap-2">
                  <Input
                    value={channelInput}
                    onChange={(e) => setChannelInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const trimmed = channelInput.trim().replace(/\s+/g, "-");
                        if (trimmed && !channels.includes(trimmed)) {
                          const newChannels = [...channels, trimmed];
                          setChannels(newChannels);
                          setChannelInput("");
                          if (isEditing) {
                            saveChannels(newChannels);
                          } else {
                            setHasChanges(true);
                          }
                        }
                      }
                    }}
                    placeholder="Enter channel name and press Enter (e.g., marketing, social-media)"
                    maxLength={64}
                  />
                  <Button
                    type="dashed"
                    onClick={() => {
                      const trimmed = channelInput.trim().replace(/\s+/g, "-");
                      if (trimmed && !channels.includes(trimmed)) {
                        const newChannels = [...channels, trimmed];
                        setChannels(newChannels);
                        setChannelInput("");
                        if (isEditing) {
                          saveChannels(newChannels);
                        } else {
                          setHasChanges(true);
                        }
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                {/* Channel tags */}
                {channels.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {channels.map((ch) => (
                      <Tag
                        key={ch}
                        color="blue"
                        closable
                        onClose={() => {
                          const newChannels = channels.filter((c) => c !== ch);
                          setChannels(newChannels);
                          if (isEditing) {
                            saveChannels(newChannels);
                          } else {
                            setHasChanges(true);
                          }
                        }}
                        className="text-sm py-1 px-3 "
                      >
                        {ch}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            </Form.Item>

            <Form.Item
              name="title"
              label={
                <span>
                  Survey Title{" "}
                  <span className="text-gray-400 text-sm font-normal">
                    (at least one language required)
                  </span>
                </span>
              }
            >
              <MultilingualInput
                placeholder="Enter survey title"
                enabledLanguages={supportedLanguages}
              />
            </Form.Item>

            <Form.Item
              name="welcomeMessage"
              label={
                <span>
                  Welcome Message{" "}
                  <span className="text-gray-400 text-sm font-normal">
                    (optional)
                  </span>
                </span>
              }
            >
              <MultilingualRichTextInput
                placeholder="Enter welcome message"
                enabledLanguages={supportedLanguages}
              />
            </Form.Item>

            <Form.Item
              name="successMessage"
              label={
                <span>
                  Success Message{" "}
                  <span className="text-gray-400 text-sm font-normal">
                    (optional - shown after survey submission)
                  </span>
                </span>
              }
            >
              <MultilingualRichTextInput
                placeholder="Enter success message"
                enabledLanguages={supportedLanguages}
              />
            </Form.Item>

            {/* Success Screen Button */}
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-3">
                Success Screen Button{" "}
                <span className="text-gray-400 font-normal">
                  (optional - adds a button below the success message)
                </span>
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <Form.Item
                  name="successButtonLabel"
                  label="Button Label"
                  className="mb-0"
                >
                  <MultilingualInput
                    placeholder="Enter button label (e.g., Visit Our Website)"
                    enabledLanguages={supportedLanguages}
                  />
                </Form.Item>
                <Form.Item label="Button URL" className="mb-0">
                  <Input
                    value={successButtonUrl}
                    onChange={(e) => {
                      setSuccessButtonUrl(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="https://example.com"
                    maxLength={512}
                  />
                </Form.Item>
              </div>
            </div>

            <Form.Item name="isActive" valuePropName="checked" label="Active">
              <Switch />
            </Form.Item>

            {/* Branding (only show when editing) */}
            {isEditing && survey && (
              <div className="border-t mt-2 mb-4">
                <h3 className="text-base font-medium mb-2">Branding</h3>
                <p className="text-gray-500 mb-4 text-sm">
                  Customize the look of your public survey page with custom
                  background and logo images. Leave empty to use the default
                  branding.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Background Image */}
                  <div>
                    <label className="block font-medium mb-2 text-sm">
                      Background Image
                    </label>
                    <div className="flex flex-col gap-3">
                      {backgroundImageUrl && (
                        <div className="relative">
                          <img
                            src={backgroundImageUrl}
                            alt="Background preview"
                            className="w-full h-32 object-cover rounded border"
                          />
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            className="absolute top-2 right-2 bg-white/80"
                            onClick={() => {
                              setBackgroundImageUrl("");
                              setHasChanges(true);
                            }}
                          />
                        </div>
                      )}
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={async (file) => {
                          try {
                            setUploadingBackground(true);
                            const result = await surveyApi.uploadBrandingImage(
                              file
                            );
                            if (result.data?.url) {
                              setBackgroundImageUrl(result.data.url);
                              setHasChanges(true);
                              message.success("Background image uploaded");
                            }
                          } catch (error) {
                            console.error("Error uploading background:", error);
                            message.error("Failed to upload background image");
                          } finally {
                            setUploadingBackground(false);
                          }
                          return false; // Prevent automatic upload
                        }}
                        disabled={uploadingBackground}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          loading={uploadingBackground}
                        >
                          {backgroundImageUrl
                            ? "Change Background"
                            : "Upload Background"}
                        </Button>
                      </Upload>
                      <p className="text-gray-400 text-xs">
                        Recommended: 1920x1080 or larger
                      </p>
                    </div>
                  </div>

                  {/* Logo Image */}
                  <div>
                    <label className="block font-medium mb-2 text-sm">
                      Logo Image
                    </label>
                    <div className="flex flex-col gap-3">
                      {logoImageUrl && (
                        <div className="relative inline-block">
                          <div className="bg-gray-800 p-4 rounded w-full inline-block">
                            <img
                              src={logoImageUrl}
                              alt="Logo preview"
                              className="h-20 w-full object-contain"
                            />
                          </div>
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            className="absolute top-2 right-2 bg-white/80"
                            onClick={() => {
                              setLogoImageUrl("");
                              setHasChanges(true);
                            }}
                          />
                        </div>
                      )}
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={async (file) => {
                          try {
                            setUploadingLogo(true);
                            const result = await surveyApi.uploadBrandingImage(
                              file
                            );
                            if (result.data?.url) {
                              setLogoImageUrl(result.data.url);
                              setHasChanges(true);
                              message.success("Logo image uploaded");
                            }
                          } catch (error) {
                            console.error("Error uploading logo:", error);
                            message.error("Failed to upload logo image");
                          } finally {
                            setUploadingLogo(false);
                          }
                          return false; // Prevent automatic upload
                        }}
                        disabled={uploadingLogo}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          loading={uploadingLogo}
                        >
                          {logoImageUrl ? "Change Logo" : "Upload Logo"}
                        </Button>
                      </Upload>
                      <p className="text-gray-400 text-xs">
                        Recommended: SVG or PNG with transparent background
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                disabled={!isFormValid || (isEditing && !hasChanges)}
                icon={<SaveOutlined />}
              >
                {isEditing ? "Save Changes" : "Create Survey"}
              </Button>
              {!isFormValid && (
                <span className="ml-3 text-amber-500 text-sm">
                  Please fill in at least one language for the title
                </span>
              )}
            </Form.Item>
          </Form>
        </Card>

        {/* Sections (only show when editing) */}
        {isEditing && survey && (
          <Card
            title={
              <span>
                Sections
                {reordering && <Spin size="small" className="ml-2" />}
              </span>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingSection(null);
                  setSectionModalOpen(true);
                }}
              >
                Add Section
              </Button>
            }
          >
            {!survey.sections || survey.sections.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No sections yet. Click "Add Section" to get started.
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSectionDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext
                  items={survey.sections
                    .sort((a, b) => a.order - b.order)
                    .map((s) => s.id!.toString())}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-6">
                    {survey.sections
                      .sort((a, b) => a.order - b.order)
                      .map((section, sectionIndex) => (
                        <SortableSectionCard
                          key={section.id}
                          section={section}
                          sectionIndex={sectionIndex}
                          onEdit={() => {
                            setEditingSection(section);
                            setSectionModalOpen(true);
                          }}
                          onDelete={() => handleDeleteSection(section.id!)}
                          isExpanded={expandedSections.has(section.id!)}
                          onToggleExpand={() =>
                            toggleSectionExpanded(section.id!)
                          }
                        >
                          {!section.questions ||
                          section.questions.length === 0 ? (
                            <div className="text-center py-4 text-gray-400 text-sm">
                              No questions in this section
                            </div>
                          ) : (
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(event) =>
                                handleSectionQuestionDragEnd(section.id!, event)
                              }
                              modifiers={[restrictToVerticalAxis]}
                            >
                              <SortableContext
                                items={section.questions
                                  .sort((a, b) => a.order - b.order)
                                  .map((q) => q.id!.toString())}
                                strategy={verticalListSortingStrategy}
                              >
                                <Table
                                  dataSource={section.questions.sort(
                                    (a, b) => a.order - b.order
                                  )}
                                  rowKey={(record) => record.id!.toString()}
                                  pagination={false}
                                  size="small"
                                  components={{
                                    body: {
                                      row: SortableRow,
                                    },
                                  }}
                                  columns={[
                                    {
                                      title: "",
                                      key: "drag",
                                      width: 40,
                                      render: (_, record) => (
                                        <DragHandleCell
                                          rowKey={record.id!.toString()}
                                        />
                                      ),
                                    },
                                    {
                                      title: "#",
                                      key: "order",
                                      width: 50,
                                      render: (_, __, index) => index + 1,
                                    },
                                    {
                                      title: "Question",
                                      key: "title",
                                      render: (_, record) =>
                                        record.title.en ||
                                        Object.values(record.title).find(
                                          (v) => v
                                        ) ||
                                        "Untitled",
                                    },
                                    {
                                      title: "Type",
                                      key: "type",
                                      width: 180,
                                      render: (_, record) => (
                                        <Tag color="blue">
                                          {QUESTION_TYPE_LABELS[record.type]}
                                        </Tag>
                                      ),
                                    },
                                    {
                                      title: "Required",
                                      key: "isRequired",
                                      width: 100,
                                      render: (_, record) =>
                                        record.isRequired ? (
                                          <Tag color="red">Required</Tag>
                                        ) : (
                                          <Tag>Optional</Tag>
                                        ),
                                    },
                                    {
                                      title: "Options",
                                      key: "options",
                                      width: 100,
                                      render: (_, record) =>
                                        record.options.length > 0 ? (
                                          <Tag color="green">
                                            {record.options.length} options
                                          </Tag>
                                        ) : (
                                          "-"
                                        ),
                                    },
                                    {
                                      title: "Conditional",
                                      key: "conditional",
                                      width: 100,
                                      render: (_, record) =>
                                        record.conditionQuestionId ? (
                                          <Tag color="purple">Conditional</Tag>
                                        ) : (
                                          "-"
                                        ),
                                    },
                                    {
                                      title: "Actions",
                                      key: "actions",
                                      width: 100,
                                      render: (_, record) => (
                                        <Space>
                                          <Button
                                            type="text"
                                            icon={<EditOutlined />}
                                            onClick={() => {
                                              setCurrentSectionId(section.id!);
                                              setEditingQuestion(record);
                                              setQuestionModalOpen(true);
                                            }}
                                          />
                                          <Popconfirm
                                            title="Delete Question"
                                            description="Are you sure you want to delete this question?"
                                            onConfirm={() =>
                                              handleDeleteQuestion(record.id!)
                                            }
                                            okText="Delete"
                                            okButtonProps={{ danger: true }}
                                            cancelText="Cancel"
                                          >
                                            <Button
                                              type="text"
                                              danger
                                              icon={<DeleteOutlined />}
                                            />
                                          </Popconfirm>
                                        </Space>
                                      ),
                                    },
                                  ]}
                                />
                              </SortableContext>
                            </DndContext>
                          )}
                          <div className="mt-4">
                            <Button
                              type="dashed"
                              icon={<PlusOutlined />}
                              onClick={() =>
                                handleAddQuestionToSection(section.id!)
                              }
                              block
                            >
                              Add Question to this Section
                            </Button>
                          </div>
                        </SortableSectionCard>
                      ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </Card>
        )}

        {/* Public Link Card */}
        {isEditing && survey && (
          <Card title="Public Survey Links" className="mt-6">
            <Tabs
              type="card"
              items={[
                {
                  key: "no-channel",
                  label: "No Channel",
                  children: (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <code className="flex-1 bg-gray-100 p-3 rounded text-sm">
                          {`${window.location.origin}/s/${survey.publicId}`}
                        </code>
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(
                              `${window.location.origin}/s/${survey.publicId}`
                            );
                            setAlert("Link copied to clipboard!", "success");
                          }}
                        >
                          Copy Link
                        </Button>
                        <Button
                          type="primary"
                          onClick={() =>
                            window.open(`/s/${survey.publicId}`, "_blank")
                          }
                        >
                          Preview
                        </Button>
                        <Button
                          onClick={() =>
                            window.open(
                              `/s/${survey.publicId}?internal_test=1`,
                              "_blank"
                            )
                          }
                        >
                          Internal Testing
                        </Button>
                      </div>
                    </div>
                  ),
                },
                ...channels.map((ch) => ({
                  key: ch,
                  label: ch,
                  children: (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <code className="flex-1 bg-gray-100 p-3 rounded text-sm">
                          {`${window.location.origin}/s/${ch}/${survey.publicId}`}
                        </code>
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(
                              `${window.location.origin}/s/${ch}/${survey.publicId}`
                            );
                            setAlert("Link copied to clipboard!", "success");
                          }}
                        >
                          Copy Link
                        </Button>
                        <Button
                          type="primary"
                          onClick={() =>
                            window.open(`/s/${ch}/${survey.publicId}`, "_blank")
                          }
                        >
                          Preview
                        </Button>
                        <Button
                          onClick={() =>
                            window.open(
                              `/s/${ch}/${survey.publicId}?internal_test=1`,
                              "_blank"
                            )
                          }
                        >
                          Internal Testing
                        </Button>
                      </div>
                    </div>
                  ),
                })),
              ]}
            />
          </Card>
        )}

        {/* Language-Specific Links Card */}
        {isEditing && survey && (
          <Card
            title={
              <span className="flex items-center gap-2">
                <LinkOutlined />
                Language-Specific Links
              </span>
            }
            className="mt-6"
          >
            <p className="text-gray-500 mb-4">
              Share these links to open the survey in a specific language. The
              language parameter will pre-select the language for respondents.
            </p>
            <Tabs
              type="card"
              items={[
                {
                  key: "no-channel",
                  label: "No Channel",
                  children: (
                    <>
                      <div className="space-y-3">
                        {SUPPORTED_LANGUAGES.filter((lang) =>
                          supportedLanguages.includes(lang.code)
                        ).map((lang) => {
                          const baseUrl = `${window.location.origin}/s/${survey.publicId}`;
                          const langUrl = `${baseUrl}?language=${lang.code}`;

                          return (
                            <div
                              key={lang.code}
                              className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                            >
                              <Tag color="blue" className="min-w-[60px] text-center">
                                {lang.code.toUpperCase()}
                              </Tag>
                              <span className="text-gray-600 font-medium min-w-[100px]">
                                {lang.label}
                              </span>
                              <code className="flex-1 bg-white p-2 rounded text-xs text-gray-700 border border-gray-200 overflow-hidden text-ellipsis">
                                {langUrl}
                              </code>
                              <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                  navigator.clipboard.writeText(langUrl);
                                  setAlert(
                                    `${lang.label} link copied to clipboard!`,
                                    "success"
                                  );
                                }}
                              >
                                Copy
                              </Button>
                              <Button
                                size="small"
                                type="link"
                                onClick={() => window.open(langUrl, "_blank")}
                              >
                                Open
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <Button
                          type="dashed"
                          block
                          icon={<CopyOutlined />}
                          onClick={() => {
                            const baseUrl = `${window.location.origin}/s/${survey.publicId}`;
                            const allLinks = SUPPORTED_LANGUAGES.filter((lang) =>
                              supportedLanguages.includes(lang.code)
                            )
                              .map(
                                (lang) =>
                                  `${lang.label} (${lang.code.toUpperCase()}): ${baseUrl}?language=${lang.code}`
                              )
                              .join("\n");
                            navigator.clipboard.writeText(allLinks);
                            setAlert(
                              "All language links copied to clipboard!",
                              "success"
                            );
                          }}
                        >
                          Copy All Links
                        </Button>
                      </div>
                    </>
                  ),
                },
                ...channels.map((ch) => ({
                  key: ch,
                  label: ch,
                  children: (
                    <>
                      <div className="space-y-3">
                        {SUPPORTED_LANGUAGES.filter((lang) =>
                          supportedLanguages.includes(lang.code)
                        ).map((lang) => {
                          const baseUrl = `${window.location.origin}/s/${ch}/${survey.publicId}`;
                          const langUrl = `${baseUrl}?language=${lang.code}`;

                          return (
                            <div
                              key={lang.code}
                              className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                            >
                              <Tag color="blue" className="min-w-[60px] text-center">
                                {lang.code.toUpperCase()}
                              </Tag>
                              <span className="text-gray-600 font-medium min-w-[100px]">
                                {lang.label}
                              </span>
                              <code className="flex-1 bg-white p-2 rounded text-xs text-gray-700 border border-gray-200 overflow-hidden text-ellipsis">
                                {langUrl}
                              </code>
                              <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                  navigator.clipboard.writeText(langUrl);
                                  setAlert(
                                    `${lang.label} link copied to clipboard!`,
                                    "success"
                                  );
                                }}
                              >
                                Copy
                              </Button>
                              <Button
                                size="small"
                                type="link"
                                onClick={() => window.open(langUrl, "_blank")}
                              >
                                Open
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <Button
                          type="dashed"
                          block
                          icon={<CopyOutlined />}
                          onClick={() => {
                            const baseUrl = `${window.location.origin}/s/${ch}/${survey.publicId}`;
                            const allLinks = SUPPORTED_LANGUAGES.filter((lang) =>
                              supportedLanguages.includes(lang.code)
                            )
                              .map(
                                (lang) =>
                                  `${lang.label} (${lang.code.toUpperCase()}): ${baseUrl}?language=${lang.code}`
                              )
                              .join("\n");
                            navigator.clipboard.writeText(allLinks);
                            setAlert(
                              "All language links copied to clipboard!",
                              "success"
                            );
                          }}
                        >
                          Copy All Links
                        </Button>
                      </div>
                    </>
                  ),
                })),
              ]}
            />
          </Card>
        )}

        {/* Supported Languages */}
        <Card title="Supported Languages" className="my-6">
          <p className="text-gray-500 mb-4">
            Select which languages this survey will support. Only selected
            languages will be available for translations and shown to survey
            respondents.
          </p>
          <div className="flex flex-wrap gap-4">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <Checkbox
                key={lang.code}
                checked={supportedLanguages.includes(lang.code)}
                onChange={(e) =>
                  handleLanguageChange(lang.code, e.target.checked)
                }
              >
                <span className="font-medium">{lang.label}</span>
                <span className="text-gray-400 ml-1">
                  ({lang.code.toUpperCase()})
                </span>
              </Checkbox>
            ))}
          </div>
        </Card>

        {/* Translation Management Card */}
        {isEditing && survey && (
          <Card title="Translation Management" className="mt-6">
            <p className="text-gray-500 mb-4">
              Export translations to an Excel file for your translation team to
              work on. After translations are complete, import the updated Excel
              file to apply them to the survey.
            </p>
            <div className="flex items-center gap-4">
              <Button
                icon={<UploadOutlined />}
                onClick={handleExportTranslations}
                loading={exportingTranslations}
              >
                Export Translations
              </Button>
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleImportTranslations(file);
                  return false; // Prevent automatic upload
                }}
                disabled={importingTranslations}
              >
                <Button
                  icon={<DownloadOutlined />}
                  loading={importingTranslations}
                >
                  Import Translations
                </Button>
              </Upload>
            </div>
            <div className="mt-4 text-sm text-gray-400">
              <p className="font-medium mb-2">Excel file format:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  First column: <code className="bg-gray-100 px-1">key</code>{" "}
                  (unique identifier for each text field)
                </li>
                <li>
                  Language columns: <code className="bg-gray-100 px-1">en</code>
                  , <code className="bg-gray-100 px-1">fr</code>,{" "}
                  <code className="bg-gray-100 px-1">ko</code>,{" "}
                  <code className="bg-gray-100 px-1">vi</code>,{" "}
                  <code className="bg-gray-100 px-1">zh</code>,{" "}
                  <code className="bg-gray-100 px-1">th</code>,{" "}
                  <code className="bg-gray-100 px-1">es</code>,{" "}
                  <code className="bg-gray-100 px-1">ru</code>,{" "}
                  <code className="bg-gray-100 px-1">hi</code>
                </li>
              </ul>
            </div>
          </Card>
        )}
      </div>

      {/* Question Modal */}
      <QuestionModal
        open={questionModalOpen}
        onClose={() => {
          setQuestionModalOpen(false);
          setEditingQuestion(null);
          setCurrentSectionId(null);
        }}
        onSave={
          editingQuestion
            ? handleUpdateQuestion
            : currentSectionId
            ? handleAddQuestionWithSection
            : handleAddQuestion
        }
        editingQuestion={editingQuestion}
        enabledLanguages={supportedLanguages}
        allQuestions={
          survey
            ? [
                // Questions from sections
                ...(survey.sections?.flatMap((s) => s.questions || []) || []),
                // Legacy questions not in sections
                ...(survey.questions?.filter((q) => !q.sectionId) || []),
              ]
            : []
        }
      />

      {/* Section Modal */}
      <SectionModal
        open={sectionModalOpen}
        onClose={() => {
          setSectionModalOpen(false);
          setEditingSection(null);
        }}
        onSave={editingSection ? handleUpdateSection : handleAddSection}
        editingSection={editingSection}
        enabledLanguages={supportedLanguages}
      />
    </AntPageContainer>
  );
};
